/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, signal, Output, EventEmitter, input, effect } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { DesyAngularModule } from 'desy-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import { getError } from '@/app/utils/form-errors';
import { AiSystemDeployment, Step5Deployment } from '@/app/interfaces/aiSystemDeployment';
import { forceAtLeastOneFieldFilled, setupConditionalRequiredValidation } from '../helpers/validation.helper';
import { binaryOptions } from '../helpers/binary-options.interface';

@Component({
  selector: 'app-deploy-system-of-ia-step-5',
  templateUrl: './deploy-system-of-ia-step-5.component.html',
  styleUrls: ['./deploy-system-of-ia-step-5.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AutoCompleteModule,
    ButtonsComponent,
    DesyAngularModule,
    DatePickerModule,
    SelectButtonModule,
    MultiSelectModule,
  ],
  providers: [ConfirmationService],
})
export class DeploySystemOfIaStep5Component {
  @Output() stepCompleted = new EventEmitter<Step5Deployment>();
  @Output() goToPreviousStep = new EventEmitter<Step5Deployment | null>();

  existingData = input<Partial<AiSystemDeployment>>();

  form: FormGroup;
  getError = getError;

  binaryOptions = binaryOptions;

  formSubmitted = signal(false);
  aiSystemUpdateRequired = signal(false);
  latencyPerformanceRequired = signal(false);
  scalabilityRequired = signal(false);
  contingencyPlanRequired = signal(false);
  cybersecurityRequired = signal(false);
  cybersecurityExtraFilled = signal(false);
  accessControlRequired = signal(false);
  attackProtectionRequired = signal(false);
  attackProtectionExtraFilled = signal(false);
  resilienceRequired = signal(false);
  resilienceExtraFilled = signal(false);

  constructor(
    private fb: FormBuilder,
    private router: Router
  ) {
    this.form = this.fb.group({
      aiSystemUpdate: [null, Validators.required],
      aiSystemUpdateDescription: [null, Validators.maxLength(500)],
      latencyPerformance: [null, Validators.required],
      latencyPerformanceDescription: [null, Validators.maxLength(500)],
      scalability: [null, Validators.required],
      scalabilityCsv: null,
      contingencyPlan: [null, Validators.required],
      contingencyPlanCsv: null,
      cybersecurity: [null, Validators.required],
      cybersecurityCsv: null,
      cybersecurityDescription: [null, Validators.maxLength(500)],
      accessControl: [null, Validators.required],
      accessControlDescription: [null, Validators.maxLength(500)],
      attackProtection: [null, Validators.required],
      attackProtectionCsv: null,
      attackProtectionDescription: [null, Validators.maxLength(500)],
      resilience: [null, Validators.required],
      resilienceCsv: null,
      resilienceDescription: [null, Validators.maxLength(500)],
    });

    setupConditionalRequiredValidation(
      this.form,
      this.aiSystemUpdateRequired,
      ['aiSystemUpdateDescription'],
      ['aiSystemUpdate']
    );

    setupConditionalRequiredValidation(
      this.form,
      this.latencyPerformanceRequired,
      ['latencyPerformanceDescription'],
      ['latencyPerformance']
    );

    setupConditionalRequiredValidation(this.form, this.scalabilityRequired, ['scalabilityCsv'], ['scalability']);

    setupConditionalRequiredValidation(
      this.form,
      this.contingencyPlanRequired,
      ['contingencyPlanCsv'],
      ['contingencyPlan']
    );

    setupConditionalRequiredValidation(this.form, this.cybersecurityRequired, null, ['cybersecurity']);
    forceAtLeastOneFieldFilled(this.form, this.cybersecurityRequired, this.cybersecurityExtraFilled, [
      'cybersecurityCsv',
      'cybersecurityDescription',
    ]);

    setupConditionalRequiredValidation(
      this.form,
      this.accessControlRequired,
      ['accessControlDescription'],
      ['accessControl']
    );

    setupConditionalRequiredValidation(this.form, this.attackProtectionRequired, null, ['attackProtection']);
    forceAtLeastOneFieldFilled(this.form, this.attackProtectionRequired, this.attackProtectionExtraFilled, [
      'attackProtectionCsv',
      'attackProtectionDescription',
    ]);

    setupConditionalRequiredValidation(this.form, this.resilienceRequired, null, ['resilience']);
    forceAtLeastOneFieldFilled(this.form, this.resilienceRequired, this.resilienceExtraFilled, [
      'resilienceCsv',
      'resilienceDescription',
    ]);

    effect(() => {
      const data = this.existingData();
      if (data) {
        this.form.patchValue(data);
      }
    });
  }

  async onSave() {
    const formData = this.form.value;
    this.stepCompleted.emit(formData);
  }

  backPage(): void {
    const formData = this.form.value;
    this.goToPreviousStep.emit(formData);
  }

  cancel(): void {
    this.router.navigate([`/catalogo-ia/sistemas-de-ia/listado-sistemas`]);
  }

  confirm() {
    this.formSubmitted.set(true);
    this.onSave();
  }
}
